import { useState, useEffect } from "react";
import { Menu, X, LogIn, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COIN } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";

import { FEATURES } from "@/lib/features";

const NAV_KEYS = [
  { key: "mission",    href: "#about",     id: "mission" },
  { key: "tokenomics", href: "#tokenomics", id: "tokenomics" },
  { key: "roadmap",    href: "#roadmap",    id: "roadmap" },
  { key: "passport",   href: "#alien-id",   id: "alien" },
  ...(FEATURES.explorer ? [{ key: "explorer", href: "/explorer", id: "explorer" }] : []),
  { key: "ranking",    href: "/ranking",    id: "ranking" },
  { key: "pilot",      href: "/pilot",      id: "pilot" },
  { key: "faq",        href: "#faq",        id: "faq" },
];

export default function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, login, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-background/80 border-b border-primary/20" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <a href="#hero" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img
            src={COIN.logo}
            alt="UFO Coin"
            className="h-10 w-10 rounded-full ring-1 ring-primary/40 group-hover:ring-primary/70 transition"
          />
          <span className="font-display font-black tracking-tighter text-lg hidden sm:inline">
            UFO<span className="text-primary">/</span>COIN
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV_KEYS.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.id}`}
              className="font-mono-alt text-xs tracking-widest transition-colors text-muted-foreground hover:text-primary whitespace-nowrap"
            >
              {t(`header.nav.${n.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            asChild
            data-testid="header-cta-buy"
            className="hidden xl:inline-flex rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt text-xs tracking-widest px-4"
          >
            <a href="#how-to-buy">{t("header.buy_cta")}</a>
          </Button>
          {user ? (
            <>
              <a
                href="/carteira"
                data-testid="header-my-account"
                className="hidden xl:inline-flex items-center gap-2 rounded-sm border border-secondary/50 bg-black/60 hover:bg-secondary/10 text-secondary font-mono-alt tracking-widest text-[10px] px-3 py-2 transition-colors whitespace-nowrap max-w-[140px] truncate"
                title={user.email}
              >
                <UserIcon className="h-3.5 w-3.5 shrink-0" />
                {(user.name || user.email || "PILOTO").slice(0, 12).toUpperCase()}
              </a>
              <button
                onClick={logout}
                data-testid="header-logout"
                className="hidden xl:inline-flex text-muted-foreground hover:text-primary font-mono-alt tracking-widest text-[10px] px-2 py-2"
              >
                {t("header.logout")}
              </button>
            </>
          ) : (
            <button
              onClick={login}
              data-testid="header-login"
              className="hidden xl:inline-flex items-center gap-2 rounded-sm border border-primary/50 bg-black/40 hover:bg-primary/10 text-primary font-mono-alt tracking-widest text-[10px] px-3 py-2 transition-colors whitespace-nowrap"
            >
              <LogIn className="h-3.5 w-3.5" /> {t("header.login_cta")}
            </button>
          )}
          <div className="hidden md:block"><LanguageSelector /></div>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center h-9 w-9 rounded-sm border border-primary/30 text-primary"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden border-t border-primary/20 bg-background/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_KEYS.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="font-mono-alt text-sm tracking-widest text-muted-foreground hover:text-primary"
              >
                {t(`header.nav.${n.key}`)}
              </a>
            ))}
            <div className="flex items-center justify-center py-1"><LanguageSelector /></div>
            <Button asChild className="w-full rounded-sm bg-primary text-primary-foreground font-mono-alt tracking-widest">
              <a href="#how-to-buy" onClick={() => setOpen(false)}>{t("header.buy_cta")}</a>
            </Button>
            {user ? (
              <>
                <a href="/carteira" onClick={() => setOpen(false)}
                  className="w-full text-center rounded-sm border border-secondary/50 text-secondary font-mono-alt tracking-widest text-xs py-2">
                  {t("header.my_account")}
                </a>
                <button onClick={() => { logout(); setOpen(false); }}
                  className="w-full text-center text-muted-foreground font-mono-alt tracking-widest text-xs py-2">
                  {t("header.logout")}
                </button>
              </>
            ) : (
              <button onClick={() => { login(); setOpen(false); }}
                data-testid="mobile-header-login"
                className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-primary/50 text-primary font-mono-alt tracking-widest text-xs py-2">
                <LogIn className="h-3.5 w-3.5" /> {t("header.login_cta")}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
